"use client"

import { useState, useEffect } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import CustomerService from "@/api/customers.service";
import CompanyService from "@/api/companies.service";
import countries from "@/components/Countries"
import { Input } from "@/components/ui/input"
import { SendIcon, Loader2, ArrowLeft, Search, Building, User, Mail, Phone, MapPin, Globe, Calendar, Percent } from "lucide-react"
import { useToast } from "@/hooks/use-toast";
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { TaxTypeSelector } from '@/components/tax/TaxTypeSelector';
import TaxesService from '@/api/taxes.service';
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  emailAddress: z.string().optional(),
  phoneNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  companyPublicId: z.string().optional(),
  companyName: z.string().optional(),
  companyEmail: z.string().optional(),
  companyPhone: z.string().optional(),
  companyAddress: z.string().optional(),
  companyCity: z.string().optional(),
  companyState: z.string().optional(),
  companyCountry: z.string().optional(),
  companyPostalCode: z.string().optional(),
  companyWebsite: z.string().optional(),
})
 
export type Customer = {
  publicId: string
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber: string
  dateOfBirth?: string
  street: string
  city: string
  state: string
  zipCode?: string
  country: string
  companyPublicId?: string
  companyName?: string
  companyEmail?: string
  companyPhone?: string
  companyAddress?: string
  companyCity?: string
  companyState?: string
  companyCountry?: string
  companyPostalCode?: string
  companyWebsite?: string
}

export default function CreateCustomerForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchCompany, setSearchCompany] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [companyData, setCompanyData] = useState([]);
  const [taxType, setTaxType] = useState<any>(null)
  const [taxIdentifier, setTaxIdentifier] = useState('')
  const [taxCountry, setTaxCountry] = useState('')
  const [taxNotes, setTaxNotes] = useState('')
  
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      phoneNumber: "",
      dateOfBirth: "",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      companyPublicId: "",
      companyName: "",
      companyEmail: "",
      companyPhone: "",
      companyAddress: "",
      companyCity: "",
      companyState: "",
      companyCountry: "",
      companyPostalCode: "",
      companyWebsite: "",
    },
  })

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchCompany.length > 0) {
        handleSearchCompany();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchCompany]);

  const handleSearchCompany = async () => {
    setIsSearching(true);
    try {
      const response = await CompanyService.searchCompany(selectedBusiness.id, searchCompany);
      const { data } = response;
      if (data.result === 'success') {
        setCompanyData(data.companies);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  };

  const { toast } = useToast();
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      // Remove dateOfBirth if it's empty
      const submitValues = {...values};
      if (!submitValues.dateOfBirth) {
        delete submitValues.dateOfBirth;
      }
      
      const response = await CustomerService.addCustomer(selectedBusiness.id, submitValues);
      const result = response.data;
      if (result.result === "success") {
        // Check if we need to add tax information
        if (taxType && taxIdentifier && taxCountry) {
          try {
            const taxPayload = {
              taxIdentifier: taxIdentifier,
              taxIdentifierType: taxType.code,
              taxTypeId: taxType.id,
              country: taxCountry,
              notes: taxNotes,
              customerId: result.customer.id,
              isVerified: false
            };
            
            await TaxesService.createCustomerTaxInfo(taxPayload);
          } catch (error) {
            console.error("Failed to add tax information", error);
            // We don't want to fail the whole customer creation if tax info fails
            toast({
              title: "Customer created",
              description: "The customer was created but tax information could not be added",
              variant: "destructive",
              duration: 5000,
            });
          }
        }
        
        toast({
          title: "Customer created",
          description: "The customer has been added successfully",
          duration: 3000,
        });

        form.reset();
        // Reset tax form fields
        setTaxType(null);
        setTaxIdentifier('');
        setTaxCountry('');
        setTaxNotes('');
      } else {
        toast({
          title: "Something went wrong",
          description: result.message || "Failed to add customer",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Something went wrong",
        description: "Failed to add customer",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Add New Customer</h1>
          <p className="mt-1 text-sm text-gray-500">Create a new customer record in your system</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/customers'}
          className="flex items-center gap-2 h-10"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Customers
        </Button>
      </div>

      <Card className="overflow-hidden bg-white shadow-sm border-0 rounded-xl">
        <CardContent className="p-0">
          <Tabs defaultValue="personal" className="w-full">
            <div className="border-b">
              <div className="px-4 sm:px-6 pt-4">
                <TabsList className="grid w-full grid-cols-3 mb-0 bg-transparent">
                  <TabsTrigger 
                    value="personal" 
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:shadow-none rounded-none py-3"
                  >
                    <User className="w-4 h-4 mr-2" />
                    <span className="text-sm">Personal Info</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="company" 
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:shadow-none rounded-none py-3"
                  >
                    <Building className="w-4 h-4 mr-2" />
                    <span className="text-sm">Company Details</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="tax" 
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:shadow-none rounded-none py-3"
                  >
                    <Percent className="w-4 h-4 mr-2" />
                    <span className="text-sm">Tax Information</span>
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <TabsContent value="personal" className="p-4 sm:p-6">
                  <div className="space-y-5 sm:space-y-6">
                    <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:gap-x-6 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              First Name <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter first name" 
                                {...field} 
                                className="h-11 focus:ring-blue-600 focus:border-blue-600" 
                                required 
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Last Name <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter last name" 
                                {...field} 
                                className="h-11 focus:ring-blue-600 focus:border-blue-600" 
                                required 
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="emailAddress"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Email Address
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input 
                                  type="email"
                                  placeholder="customer@example.com" 
                                  {...field} 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600" 
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Phone Number
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
                                <PhoneInput 
                                  placeholder="+1 (555) 000-0000" 
                                  {...field} 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600 w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm" 
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="dateOfBirth"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Date of Birth
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input 
                                  type="date"
                                  placeholder="YYYY-MM-DD" 
                                  {...field} 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600" 
                                />
                              </div>
                            </FormControl>
                            <p className="text-xs text-gray-500 mt-1">Optional. Can be used for automated birthday reminders.</p>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator className="my-5 sm:my-6" />
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>

                    <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:gap-x-6">
                      <FormField
                        control={form.control}
                        name="street"
                        render={({ field }) => (
                          <FormItem className="col-span-1 sm:col-span-2">
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Street Address
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input 
                                  placeholder="123 Main St" 
                                  {...field} 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600" 
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5 sm:gap-x-6">
                        <FormField
                          control={form.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                City
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="City" 
                                  {...field} 
                                  className="h-11 focus:ring-blue-600 focus:border-blue-600" 
                                />
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                State / Province
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="State" 
                                  {...field} 
                                  className="h-11 focus:ring-blue-600 focus:border-blue-600" 
                                />
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="zipCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                Zip / Postal Code
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Zip Code" 
                                  {...field} 
                                  className="h-11 focus:ring-blue-600 focus:border-blue-600" 
                                />
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2">
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Country
                            </FormLabel>
                            <FormControl>
                              <Select 
                                {...field} 
                                defaultValue={field.value ?? ''} 
                                value={field.value ?? ''} 
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger className="h-11 focus:ring-blue-600 focus:border-blue-600">
                                  <SelectValue placeholder="Select a country" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px]">
                                  {countries.map((country: any) => (
                                    <SelectItem key={country} value={country}>
                                      {country}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="company" className="p-4 sm:p-6">
                  <div className="space-y-5 sm:space-y-6">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-5 sm:mb-6">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <Building className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">Company Association</h3>
                          <p className="text-sm text-blue-700 mt-1">
                            Search for an existing company or create a new one by entering details below.
                          </p>
                        </div>
                      </div>
                    </div>

                    <FormField
                      control={form.control}
                      name="companyName"
                      render={({ field }) => (
                        <FormItem className="mb-5 sm:mb-6">
                          <FormLabel className="text-sm font-medium text-gray-700">
                            Company Name
                          </FormLabel>
                          <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-2">
                              <div className="relative flex-grow">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input 
                                  placeholder="Search for existing company" 
                                  value={searchCompany}
                                  onChange={(e) => setSearchCompany(e.target.value)}
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </div>
                              <Button 
                                type="button"
                                variant="outline"
                                onClick={handleSearchCompany} 
                                disabled={isSearching}
                                className="h-11 px-4 whitespace-nowrap"
                              >
                                {isSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                                Search
                              </Button>
                            </div>
                            
                            {companyData.length > 0 && (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                                <div className="max-h-[200px] overflow-y-auto">
                                  {companyData.map((company: any) => (
                                    <div 
                                      key={company.id}
                                      className="p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                                      onClick={() => {
                                        field.onChange(company.name);
                                        form.setValue('companyPublicId', company.publicId);
                                        form.setValue('companyEmail', company.emailAddress);
                                        form.setValue('companyPhone', company.phoneNumber);
                                        form.setValue('companyAddress', company.address);
                                        form.setValue('companyCity', company.city);
                                        form.setValue('companyState', company.state);
                                        form.setValue('companyCountry', company.country);
                                        form.setValue('companyPostalCode', company.postalCode);
                                        form.setValue('companyWebsite', company.website);
                                      }}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-medium text-gray-900">{company.name}</p>
                                          <p className="text-sm text-gray-500">{company.emailAddress}</p>
                                        </div>
                                        <Badge variant="outline" className="text-xs">Select</Badge>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="relative">
                              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="Or enter new company name" 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </FormControl>
                            </div>
                          </div>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:gap-x-6 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="companyEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Company Email
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input 
                                  type="email"
                                  {...field} 
                                  placeholder="company@example.com" 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="companyPhone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Company Phone
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
                                <PhoneInput 
                                  {...field} 
                                  placeholder="+1 (555) 000-0000" 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600 w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm"
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="companyWebsite"
                        render={({ field }) => (
                          <FormItem className="col-span-1 sm:col-span-2">
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Company Website
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input 
                                  {...field} 
                                  placeholder="https://example.com" 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator className="my-5 sm:my-6" />
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Company Address</h3>

                    <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:gap-x-6">
                      <FormField
                        control={form.control}
                        name="companyAddress"
                        render={({ field }) => (
                          <FormItem className="col-span-1 sm:col-span-2">
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Street Address
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input 
                                  {...field} 
                                  placeholder="123 Business St" 
                                  className="h-11 pl-10 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </div>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5 sm:gap-x-6">
                        <FormField
                          control={form.control}
                          name="companyCity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                City
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="City" 
                                  className="h-11 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="companyState"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                State / Province
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="State" 
                                  className="h-11 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="companyPostalCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                Postal Code
                              </FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="Postal Code" 
                                  className="h-11 focus:ring-blue-600 focus:border-blue-600"
                                />
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="companyCountry"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2">
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Country
                            </FormLabel>
                            <FormControl>
                              <Select 
                                {...field} 
                                defaultValue={field.value ?? ''} 
                                value={field.value ?? ''} 
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger className="h-11 focus:ring-blue-600 focus:border-blue-600">
                                  <SelectValue placeholder="Select a country" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px]">
                                  {countries.map((country: any) => (
                                    <SelectItem key={country} value={country}>
                                      {country}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="tax" className="p-4 sm:p-6">
                  <div className="space-y-5 sm:space-y-6">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-5 sm:mb-6">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <Percent className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">Tax Information</h3>
                          <p className="text-sm text-blue-700 mt-1">
                            Add tax identifiers for this customer to ensure proper tax handling on invoices and quotations.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="tax-type">Tax Type</Label>
                          <TaxTypeSelector
                            placeholder="Select tax type"
                            onSelect={(value) => setTaxType(value)}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="tax-identifier">Tax Identifier</Label>
                          <Input
                            id="tax-identifier"
                            placeholder="Enter tax identifier number"
                            value={taxIdentifier}
                            onChange={(e) => setTaxIdentifier(e.target.value)}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="tax-country">Tax Country</Label>
                          <Select 
                            value={taxCountry} 
                            onValueChange={setTaxCountry}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px]">
                              {countries.map((country) => (
                                <SelectItem key={country} value={country}>
                                  {country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">Country associated with this tax identifier.</p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="tax-notes">Notes (Optional)</Label>
                          <Textarea
                            id="tax-notes"
                            placeholder="Optional notes about this tax identifier"
                            value={taxNotes}
                            onChange={(e) => setTaxNotes(e.target.value)}
                            className="min-h-[100px]"
                          />
                        </div>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2">
                        You will be able to add more tax identifiers after creating the customer.
                      </p>
                    </div>
                  </div>
                </TabsContent>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end p-4 sm:p-6 border-t border-gray-100 bg-gray-50 gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full sm:w-auto h-11"
                    onClick={() => window.location.href = '/customers'}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white h-11"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                        Creating...
                      </>
                    ) : (
                      <>
                        <SendIcon className="w-4 h-4 mr-2" /> 
                        Create Customer
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
